import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, canManageUsers, type AuthUser, type Player } from '@/lib/api';
import {
  QUALITY_BAR,
  QUALITY_LABEL,
  QUALITY_TEXT,
  type TimeQuality,
  formatServerClock,
  hourQuality,
  localHourIn,
  serverClock,
  serverHourToRealDate,
} from '@/lib/serverTime';

export default function WorldClock({ user }: { user: AuthUser }) {
  const qc = useQueryClient();
  const players = useQuery({
    queryKey: ['players'],
    queryFn: () => api.listPlayers(),
  });

  const backfill = useMutation({
    mutationFn: () => api.backfillTimezones(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['players'] }),
  });

  const [now, setNow] = useState(() => new Date());
  const [offsetHours, setOffsetHours] = useState(0);

  useEffect(() => {
    if (offsetHours !== 0) return;
    const id = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(id);
  }, [offsetHours]);

  const viewDate = useMemo(() => {
    if (offsetHours === 0) return now;
    const liveServerHour = serverClock(now).h;
    const targetHour = ((liveServerHour + offsetHours) % 24 + 24) % 24;
    return serverHourToRealDate(targetHour, now);
  }, [now, offsetHours]);

  const activePlayers = useMemo(
    () => (players.data ?? []).filter((p) => p.active),
    [players.data],
  );

  const withTz = activePlayers.filter((p) => !!p.timezone) as Array<Player & { timezone: string }>;
  const withoutTz = activePlayers.filter((p) => !p.timezone);

  const buckets = useMemo(() => {
    const counts: number[] = new Array(24).fill(0);
    const names: string[][] = Array.from({ length: 24 }, () => []);
    for (const p of withTz) {
      try {
        const h = localHourIn(viewDate, p.timezone);
        counts[h]++;
        names[h].push(p.displayName || p.name);
      } catch {
        // invalid stored tz — ignore
      }
    }
    return { counts, names };
  }, [withTz, viewDate]);

  const maxCount = Math.max(1, ...buckets.counts);

  const aggregate = useMemo(() => {
    const out: Record<TimeQuality, number> = { good: 0, meh: 0, bad: 0 };
    for (let h = 0; h < 24; h++) out[hourQuality(h)] += buckets.counts[h];
    return out;
  }, [buckets.counts]);

  const browserTz =
    Intl.DateTimeFormat().resolvedOptions().timeZone || 'local';
  const myLocalTime = new Intl.DateTimeFormat('en-GB', {
    timeZone: browserTz,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(viewDate);

  const serverStr = formatServerClock(viewDate);
  const stQuality = hourQuality(serverClock(viewDate).h);

  return (
    <div className="mx-auto w-full max-w-4xl space-y-6">
      <header>
        <h1 className="text-xl font-semibold">World Clock</h1>
        <p className="text-sm text-slate-400">
          Server time is fixed at UTC−2 year-round. Player local times are
          computed from their configured timezone in the roster detail.
        </p>
      </header>

      <section className="rounded-lg border border-slate-800 bg-slate-900/40 p-5">
        <div className="flex flex-col items-center gap-3">
          <div className="text-center">
            <div
              className={`font-mono text-5xl tabular-nums tracking-tight ${QUALITY_TEXT[stQuality]}`}
            >
              {serverStr}
              <span className="ml-2 align-middle text-base font-medium text-slate-500">
                ST
              </span>
            </div>
            <div className="mt-1 text-sm text-slate-400">
              Your time: <span className="font-mono">{myLocalTime}</span>{' '}
              <span className="text-slate-500">({browserTz})</span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <StepButton onClick={() => setOffsetHours((h) => h - 1)}>−1 h</StepButton>
            <button
              onClick={() => {
                setOffsetHours(0);
                setNow(new Date());
              }}
              className={
                offsetHours === 0
                  ? 'rounded bg-emerald-600 px-3 py-1 text-xs font-medium text-white'
                  : 'rounded border border-slate-700 px-3 py-1 text-xs text-slate-300 hover:bg-slate-800'
              }
              title="Return to live server time"
            >
              {offsetHours === 0 ? 'Live' : 'Return to live'}
            </button>
            <StepButton onClick={() => setOffsetHours((h) => h + 1)}>+1 h</StepButton>
          </div>
          {offsetHours !== 0 && (
            <div className="text-xs text-amber-400">
              Simulating {offsetHours > 0 ? '+' : ''}
              {offsetHours} h from live server time
            </div>
          )}
        </div>
      </section>

      <section className="rounded-lg border border-slate-800 bg-slate-900/40 p-5">
        <div className="mb-3 flex items-end justify-between">
          <h2 className="text-sm font-medium uppercase tracking-wider text-slate-400">
            Player local time distribution
          </h2>
          <div className="text-xs text-slate-500">
            {withTz.length} with timezone
            {withoutTz.length > 0 && ` · ${withoutTz.length} unassigned`}
          </div>
        </div>

        <div className="mb-4 grid grid-cols-3 gap-2">
          <QualityTile quality="good" count={aggregate.good} total={withTz.length} hint="08–21" />
          <QualityTile quality="meh" count={aggregate.meh} total={withTz.length} hint="06–07, 22–23" />
          <QualityTile quality="bad" count={aggregate.bad} total={withTz.length} hint="00–05" />
        </div>

        <Histogram counts={buckets.counts} names={buckets.names} max={maxCount} />

        {withoutTz.length > 0 && (
          <details className="mt-4 text-xs text-slate-400">
            <summary className="cursor-pointer select-none hover:text-slate-200">
              Unassigned players ({withoutTz.length})
            </summary>
            <div className="mt-2 flex flex-wrap gap-1">
              {withoutTz.map((p) => (
                <span
                  key={p.id}
                  className="rounded bg-slate-800 px-2 py-0.5 text-slate-200"
                >
                  {p.displayName || p.name}
                </span>
              ))}
            </div>
          </details>
        )}

        {canManageUsers(user.role) && (
          <div className="mt-4 flex items-center gap-3 border-t border-slate-800 pt-3">
            <button
              onClick={() => backfill.mutate()}
              disabled={backfill.isPending}
              className="rounded bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-500 disabled:opacity-50"
            >
              {backfill.isPending
                ? 'Filling…'
                : 'Fill timezones from LastRank country'}
            </button>
            <span className="text-xs text-slate-500">
              Only fills empty values — manual entries stay untouched.
            </span>
            {backfill.data && (
              <span className="text-xs text-emerald-400">
                Updated {backfill.data.updated}
                {backfill.data.skipped.length > 0 &&
                  `, ${backfill.data.skipped.length} skipped (unknown country)`}
              </span>
            )}
          </div>
        )}
      </section>
    </div>
  );
}

function QualityTile({
  quality,
  count,
  total,
  hint,
}: {
  quality: TimeQuality;
  count: number;
  total: number;
  hint: string;
}) {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div className="rounded-md border border-slate-800 bg-slate-950/40 p-3">
      <div className={`text-xs font-medium uppercase tracking-wider ${QUALITY_TEXT[quality]}`}>
        {QUALITY_LABEL[quality]}
      </div>
      <div className="mt-1 flex items-baseline gap-2">
        <span className={`font-mono text-2xl tabular-nums ${QUALITY_TEXT[quality]}`}>{count}</span>
        <span className="text-xs text-slate-500">{pct}%</span>
      </div>
      <div className="mt-0.5 text-[10px] text-slate-500">{hint}</div>
    </div>
  );
}

function StepButton({
  onClick,
  children,
}: {
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className="rounded border border-slate-700 px-3 py-1 font-mono text-xs text-slate-200 hover:bg-slate-800"
    >
      {children}
    </button>
  );
}

function Histogram({
  counts,
  names,
  max,
}: {
  counts: number[];
  names: string[][];
  max: number;
}) {
  return (
    <div className="w-full overflow-x-auto">
      <div className="flex h-48 min-w-[600px] items-stretch gap-1">
        {counts.map((c, h) => {
          const heightPct = (c / max) * 100;
          const q = hourQuality(h);
          const tooltip =
            c === 0
              ? `${pad(h)}:00 — nobody (${QUALITY_LABEL[q]})`
              : `${pad(h)}:00 (${QUALITY_LABEL[q]}) — ${c} player${c === 1 ? '' : 's'}\n${names[h].join(', ')}`;
          return (
            <div
              key={h}
              className="group relative flex h-full flex-1 flex-col items-center justify-end"
              title={tooltip}
            >
              <div
                className={`font-mono text-[10px] tabular-nums leading-none ${c > 0 ? QUALITY_TEXT[q] : 'text-slate-600'}`}
              >
                {c}
              </div>
              <div
                className={
                  c === 0
                    ? 'mt-0.5 w-full rounded-t bg-slate-800/70'
                    : `mt-0.5 w-full rounded-t ${QUALITY_BAR[q]}`
                }
                style={{ height: c === 0 ? '2px' : `${Math.max(4, heightPct)}%` }}
              />
            </div>
          );
        })}
      </div>
      <div className="mt-1 flex min-w-[600px] gap-1 font-mono text-[10px] text-slate-500">
        {counts.map((_, h) => (
          <div key={h} className="flex-1 text-center">
            {h % 3 === 0 ? pad(h) : ''}
          </div>
        ))}
      </div>
    </div>
  );
}

function pad(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}
